import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { MessageSquare, MessageCircle, X, Send, User, ArrowUpRight, Loader2, ThumbsUp, ThumbsDown, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function FeedbackBlock({ tenantSlug, sessionId, messageId, userEmail }) {
  const [stage, setStage] = useState('idle'); // 'idle' | 'asking' | 'commenting' | 'done'
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const sendFeedback = async (chosenRating, withComment = null) => {
    if (!sessionId) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/widget/${tenantSlug}/feedback`, {
        conversation_id: sessionId,
        message_id: messageId,
        rating: chosenRating,
        comment: withComment || undefined,
        session_id: sessionId,
        user_email: userEmail || undefined,
      });
      setStage('done');
    } catch {
      // Fail silent — feedback is optional and shouldn't block conversation
      setStage('done');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRating = (r) => {
    setRating(r);
    setStage('commenting');
    // Auto-submit thumbs up if no comment; thumbs down asks for comment first
    if (r === 'up') {
      sendFeedback('up');
    }
  };

  if (stage === 'done') {
    return (
      <div className="mt-2 flex items-center gap-1.5 text-xs text-[#a1a1aa]" data-testid="feedback-thanks">
        <Check className="w-3 h-3 text-[#10B981]" />
        <span>Danke für dein Feedback.</span>
      </div>
    );
  }

  if (stage === 'commenting' && rating === 'down') {
    return (
      <div className="mt-2 space-y-1.5" data-testid="feedback-comment">
        <p className="text-[10px] text-[#a1a1aa]">Was hat gefehlt? (optional)</p>
        <textarea value={comment} onChange={e => setComment(e.target.value.slice(0, 500))}
          rows={2} maxLength={500} placeholder="Kurz beschreiben..."
          data-testid="feedback-comment-input"
          className="w-full bg-[#09090b] border border-white/10 rounded-md px-2 py-1 text-xs text-white placeholder-[#3f3f46] focus:outline-none focus:border-[#EF694C] resize-none" />
        <div className="flex gap-1.5">
          <button type="button" disabled={submitting} onClick={() => sendFeedback('down', comment)}
            data-testid="feedback-comment-submit"
            className="text-[10px] px-2 py-1 rounded bg-[#EF694C]/20 text-[#EF694C] hover:bg-[#EF694C]/30 transition-colors disabled:opacity-50">
            Senden
          </button>
          <button type="button" onClick={() => sendFeedback('down')}
            data-testid="feedback-comment-skip"
            className="text-[10px] px-2 py-1 rounded text-[#a1a1aa] hover:text-white transition-colors">
            Überspringen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2 flex items-center gap-2 text-xs text-[#a1a1aa]" data-testid="feedback-prompt">
      <span>War diese Antwort hilfreich?</span>
      <button type="button" onClick={() => handleRating('up')} disabled={submitting}
        data-testid="feedback-thumbs-up"
        className="p-1 rounded hover:bg-white/5 text-[#a1a1aa] hover:text-[#10B981] transition-colors disabled:opacity-50"
        aria-label="Ja">
        <ThumbsUp className="w-3 h-3" />
      </button>
      <button type="button" onClick={() => handleRating('down')} disabled={submitting}
        data-testid="feedback-thumbs-down"
        className="p-1 rounded hover:bg-white/5 text-[#a1a1aa] hover:text-[#F43F5E] transition-colors disabled:opacity-50"
        aria-label="Nein">
        <ThumbsDown className="w-3 h-3" />
      </button>
    </div>
  );
}

export default function WidgetPage() {
  const { tenantSlug } = useParams();
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSendening] = useState(false);
  const [error, setError] = useState(null);
  const [emailInput, setEmailInput] = useState('');
  const [emailGatePassed, setEmailGatePassed] = useState(false);
  const [escalationEmailRequired, setEscalationEmailRequired] = useState(false);
  const [escalationEmailInput, setEscalationEmailInput] = useState('');
  const [escalating, setEscalating] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/widget/${tenantSlug}/config`)
      .then(r => setConfig(r.data))
      .catch(() => setError('Widget nicht verfügbar'));
  }, [tenantSlug]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const createSession = async (email, name) => {
    const body = {};
    if (name) body.name = name;
    if (email) body.email = email;
    const r = await axios.post(`${API}/api/widget/${tenantSlug}/sessions`, body);
    setSessionId(r.data.session_id);
    if (r.data.messages?.length) setMessages(r.data.messages);
    return r.data.session_id;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailInput)) {
      setError('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      return;
    }
    setError(null);
    try {
      await createSession(emailInput, nameInput || undefined);
      setEmailGatePassed(true);
    } catch {
      setError('Verbindungsfehler. Bitte versuchen Sie es erneut.');
    }
  };

  const handleSenden = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    if (input.length > 4000) {
      setError('Nachricht ist zu lang (max. 4000 Zeichen)');
      return;
    }
    setError(null);
    const content = input.trim();
    setInput('');
    setSendening(true);

    // Optimistic user message
    const userMsg = { content, sender_type: 'customer', id: Date.now() };
    setMessages(prev => [...prev, userMsg]);

    try {
      let sid = sessionId;
      if (!sid) {
        sid = await createSession(emailInput || undefined, nameInput || undefined, { hydrateMessages: false });
      }
      const r = await axios.post(`${API}/api/widget/${tenantSlug}/sessions/${sid}/messages`, { content });
      if (r.data.ai_message) {
        setMessages(prev => [...prev, r.data.ai_message]);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.includes('4000') || detail?.includes('too long')) {
        setError('Nachricht ist zu lang (max. 4000 Zeichen)');
      } else if (err.response?.status === 429) {
        setError('Zu viele Nachrichten. Bitte warten Sie einen Moment.');
      } else {
        setError('Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.');
      }
    } finally { setSendening(false); }
  };

  const appendSystemMessage = (content) => {
    if (!content) return;
    setMessages(prev => [...prev, {
      content,
      sender_type: 'system',
      sender_name: config?.assistant_name || 'KI-Assistent',
      id: `system-${Date.now()}`,
      created_at: new Date().toISOString(),
    }]);
  };

  const handleEscalate = async (emailOverride = '') => {
    if (!sessionId || escalating) return;

    setError(null);
    setEscalating(true);

    try {
      const knownEmail = emailOverride || (emailGatePassed ? emailInput : '');
      const payload = {};

      if (knownEmail) payload.email = knownEmail;
      if (nameInput) payload.name = nameInput;

      const r = await axios.post(`${API}/api/widget/${tenantSlug}/sessions/${sessionId}/escalate`, payload);

      appendSystemMessage(r.data.message || 'Ihre Anfrage wurde weitergeleitet.');

      const needsEmail = Boolean(r.data.email_required);
      setEscalationEmailRequired(needsEmail);

      if (!needsEmail && knownEmail) {
        setEmailInput(knownEmail);
        setEmailGatePassed(true);
      }
    } catch {
      setError('Weiterleitung fehlgeschlagen');
    } finally {
      setEscalating(false);
    }
  };

  const handleEscalationEmailSubmit = async (e) => {
    e.preventDefault();

    const email = escalationEmailInput.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      setError('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      return;
    }

    setEmailInput(email);
    setEmailGatePassed(true);
    setEscalationEmailRequired(false);
    await handleEscalate(email);
  };

  if (error && !config) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <p className="text-[#a1a1aa]">{error}</p>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#EF694C] animate-spin" />
      </div>
    );
  }

  const themeColor = config.theme_color || '#EF694C';
  const isLeft = config.position === 'bottom-left';
  const requireEmail = config.require_email && !emailGatePassed && !sessionId;

  return (
    <div className="min-h-screen bg-[#09090b] flex items-end p-6" style={{ justifyContent: isLeft ? 'flex-start' : 'flex-end' }}>
      {open ? (
        <div className="w-full max-w-[400px] bg-[#121214] border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col" style={{ height: '600px' }}
          data-testid="widget-container">
          {/* Header */}
          <div className="px-5 py-4 flex items-center justify-between flex-shrink-0" style={{ backgroundColor: themeColor }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
                {config.launcher_image_url ? (
                  <img src={config.launcher_image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <MessageSquare className="w-4 h-4 text-white" />
                )}
              </div>
              <div>
                <p className="text-sm font-semibold text-white">{config.company_name}</p>
                <p className="text-xs text-white/70">KI-gestützter Support</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="widget-close-btn"
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
              <X className="w-4 h-4 text-white" />
            </button>
          </div>

          {/* Email Gate */}
          {requireEmail ? (
            <div className="flex-1 flex items-center justify-center p-6">
              <form onSubmit={handleEmailSubmit} className="w-full space-y-4" data-testid="email-gate-form">
                <div className="text-center mb-4">
                  <p className="text-sm text-white font-medium mb-1">{config.welcome_message || 'Hallo! Wie können wir Ihnen helfen?'}</p>
                  <p className="text-xs text-[#a1a1aa]">Bitte geben Sie Ihre E-Mail-Adresse ein, um den Chat zu starten.</p>
                </div>
                <input value={nameInput} onChange={e => setNameInput(e.target.value)}
                  placeholder="Ihr Name (optional)" data-testid="widget-name-input"
                  className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C] transition-all" />
                <input type="email" value={emailInput} onChange={e => setEmailInput(e.target.value)}
                  placeholder="Ihre E-Mail-Adresse" required data-testid="widget-email-input"
                  className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C] transition-all" />
                {error && <p className="text-xs text-[#F43F5E]">{error}</p>}
                <button type="submit" data-testid="widget-start-chat-btn"
                  className="w-full h-10 text-white font-semibold rounded-lg text-sm transition-all hover:opacity-90" style={{ backgroundColor: themeColor }}>
                  Chat starten
                </button>
              </form>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="widget-messages">
                {messages.length === 0 && (
                  <div className="bg-white/5 rounded-xl p-3 border border-white/5">
                    <p className="text-sm text-[#d4d4d8]">{config.welcome_message || 'Hallo! Wie können wir Ihnen helfen?'}</p>
                    <p className="text-xs text-[#a1a1aa] mt-1.5 font-medium">{config.assistant_name || 'KI-Assistent'}</p>
                  </div>
                )}
                {messages.filter(m => String(m.content || '').trim()).map((m, i) => (
                  <div key={m.id || i} className={`flex ${m.sender_type === 'customer' ? 'justify-end' : 'justify-start'}`}>
                    {m.sender_type === 'system' ? (
                      <div className="bg-[#3B82F6]/10 border border-[#3B82F6]/20 rounded-xl px-3 py-2 max-w-[85%]">
                        <p className="text-xs text-[#93C5FD]">{m.content}</p>
                      </div>
                    ) : m.sender_type === 'customer' ? (
                      <div className="rounded-xl px-3 py-2 max-w-[80%] text-white text-sm" style={{ backgroundColor: themeColor }}>
                        {m.content}
                      </div>
                    ) : (
                      <div className="bg-white/5 border border-white/5 rounded-xl px-3 py-2 max-w-[85%]">
                        <p className="text-sm text-[#d4d4d8] whitespace-pre-wrap">{m.content}</p>
                        <p className="text-[10px] text-[#52525b] mt-1">{m.sender_name || 'KI-Assistent'}</p>
                        {m.sender_type === 'ai' && m.id && sessionId && (
                          <FeedbackBlock
                            tenantSlug={tenantSlug}
                            sessionId={sessionId}
                            messageId={m.id}
                            userEmail={emailGatePassed ? emailInput : undefined}
                          />
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="bg-white/5 rounded-xl px-4 py-2">
                      <Loader2 className="w-4 h-4 text-[#a1a1aa] animate-spin" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Error banner */}
              {error && (
                <div className="px-4 pb-1">
                  <p className="text-xs text-[#F43F5E] bg-[#F43F5E]/10 rounded-lg px-3 py-1.5">{error}</p>
                </div>
              )}

              {/* Escalation */}
              {sessionId && !escalationEmailRequired && (
                <div className="px-4 py-1">
                  <button onClick={() => handleEscalate()} disabled={escalating} data-testid="widget-escalate-btn"
                    className="w-full flex items-center justify-center gap-1.5 h-8 text-xs text-[#a1a1aa] hover:text-white hover:bg-white/5 rounded-lg transition-all disabled:opacity-50">
                    <ArrowUpRight className="w-3.5 h-3.5" />{config.escalation_label || 'Mit einem Mitarbeiter sprechen'}
                  </button>
                </div>
              )}

              {/* Escalation email form — shown when no agent is online and email is missing */}
              {sessionId && escalationEmailRequired && (
                <div className="px-4 py-2">
                  <form onSubmit={handleEscalationEmailSubmit} className="space-y-2" data-testid="widget-escalation-email-form">
                    <input type="email" value={escalationEmailInput}
                      onChange={e => setEscalationEmailInput(e.target.value)}
                      placeholder="Ihre E-Mail-Adresse" required
                      data-testid="widget-escalation-email-input"
                      className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C] transition-all" />
                    <button type="submit" disabled={escalating} data-testid="widget-escalation-email-submit"
                      className="w-full h-10 text-white font-semibold rounded-lg text-sm transition-all hover:opacity-90 disabled:opacity-50"
                      style={{ backgroundColor: themeColor }}>
                      {escalating ? 'Wird gesendet...' : 'E-Mail senden'}
                    </button>
                  </form>
                </div>
              )}

              {/* Input */}
              <form onSubmit={handleSenden} className="p-3 border-t border-white/10 flex gap-2 flex-shrink-0" data-testid="widget-input-form">
                <input value={input} onChange={e => setInput(e.target.value)} disabled={sending}
                  placeholder="Nachricht eingeben..." data-testid="widget-message-input"
                  className="flex-1 h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C] transition-all disabled:opacity-50" />
                <button type="submit" disabled={!input.trim() || sending} data-testid="widget-send-btn"
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white transition-all disabled:opacity-30 hover:opacity-90" style={{ backgroundColor: themeColor }}>
                  <Send className="w-4 h-4" />
                </button>
              </form>

              {/* Footer */}
              <div className="px-4 py-2 text-center border-t border-white/5">
                <p className="text-xs text-[#71717a]">Bereitgestellt von FyreSupport</p>
              </div>
            </>
          )}
        </div>
      ) : (
        <button onClick={() => setOpen(true)} data-testid="widget-open-btn"
            className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:scale-105 transition-transform overflow-hidden"
            style={{ backgroundColor: themeColor }}>
            {config.launcher_image_url ? (
              <img src={config.launcher_image_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <MessageCircle className="w-6 h-6 text-white" />
            )}
          </button>
      )}
    </div>
  );
}
